import { getAvailableRoles, getCurrentRole } from "../utils/roleSwitcher";
import StorageService from "../services/StorageService";
import { BUYER_DASHBOARD_MODULE_ROUTES } from "./buyerDashboardModuleUi";

/** Same rule as sidebar `shouldShowRoleSection(currentRole, "buyer")`. */
export function isBuyerRoleSectionVisible(currentRole = getCurrentRole()) {
  if (currentRole == null) return true;
  return Number(currentRole.id) === 1;
}

/**
 * Buyer dashboard module tabs — visibility follows sidebar / P2P access rules.
 * @typedef {{ key: string, label: string, to: string, pathPrefixes?: string[] }} BuyerDashboardModule
 * @typedef {{ key: string, label: string, to: string, visible?: (ctx: BuyerDashboardNavContext) => boolean }} BuyerDashboardSubTab
 */

export const BUYER_DASHBOARD_MODULES = [
  {
    key: "overview",
    label: "Overview",
    to: "/dashboard/buyer",
    pathPrefixes: ["/dashboard/buyer"],
    visible: (ctx) => ctx.canAccessBuyerDashboard,
  },
  {
    key: "rfq",
    label: "RFQ & Purchase",
    to: BUYER_DASHBOARD_MODULE_ROUTES.rfq,
    pathPrefixes: [
      "/dashboard/buyer/rfq",
      "/buyer-rfq",
      "/buyer-rfq-form",
      "/seller-quotation-list",
      "/seller-quotation-accept",
      "/purchase-order-list",
      "/buyer-clarification-list",
      "/approve-invoice-list",
      "/grn-manifest-list",
      "/shipping-manifest-list",
      "/eway-bill-tracking",
    ],
    visible: (ctx) => ctx.canAccessBuyerDashboard,
  },
  {
    key: "cm",
    label: "Contract Manufacturing",
    to: BUYER_DASHBOARD_MODULE_ROUTES.cm,
    pathPrefixes: [
      "/dashboard/buyer/cm",
      "/dashboard/buyer/contract-manufacturers",
    ],
    visible: (ctx) => ctx.canAccessBuyerDashboard,
  },
  {
    key: "infra",
    label: "Infrastructure Leasing",
    to: BUYER_DASHBOARD_MODULE_ROUTES.infra,
    pathPrefixes: [
      "/dashboard/buyer/infra",
      "/infrastructure-leasing/my-requirements",
      "/infrastructure-leasing/my-enquiries",
      "/infrastructure-leasing/listings",
      "/infrastructure-leasing/post-requirement",
    ],
    visible: (ctx) => ctx.canAccessBuyerDashboard,
  },
  {
    key: "p2p",
    label: "Procure-to-Pay",
    to: BUYER_DASHBOARD_MODULE_ROUTES.p2p,
    pathPrefixes: ["/dashboard/buyer/p2p", "/dashboard/p2p", "/dashboard/admin/p2p"],
    visible: (ctx) => ctx.canAccessBuyerDashboard && ctx.hasP2pAccess && !ctx.p2pLoading,
  },
  {
    key: "logistics",
    label: "Logistics",
    to: BUYER_DASHBOARD_MODULE_ROUTES.logistics,
    pathPrefixes: ["/dashboard/buyer/logistics", "/logistics", "/logistics-rfqs-sent"],
    visible: (ctx) => ctx.canAccessBuyerDashboard,
  },
  {
    key: "qa",
    label: "Quality Inspection",
    to: BUYER_DASHBOARD_MODULE_ROUTES.qa,
    pathPrefixes: ["/dashboard/buyer/qa", "/buyer/qa-quotations-list", "/buyer-qa", "/buyer/qa-request"],
    visible: (ctx) => ctx.canAccessBuyerDashboard,
  },
  {
    key: "auctions",
    label: "Auctions",
    to: BUYER_DASHBOARD_MODULE_ROUTES.auctions,
    pathPrefixes: ["/dashboard/buyer/auctions", "/auction", "/auction/my", "/auction/detail", "/auction/bid"],
    visible: (ctx) => ctx.canAccessBuyerDashboard,
  },
];

/** Contextual sub-tabs per module (second row in mockup). */
export const BUYER_DASHBOARD_SUB_TABS = {
  rfq: [
    { key: "rfqs", label: "My RFQs", to: "/buyer-rfq" },
    { key: "create", label: "Create RFQ", to: "/buyer-rfq-form" },
    { key: "quotes", label: "Review quotes", to: "/seller-quotation-list" },
    { key: "orders", label: "Purchase orders", to: "/purchase-order-list" },
  ],
  cm: [
    { key: "browse", label: "Browse manufacturers", to: "/dashboard/buyer/contract-manufacturers" },
    { key: "quotes", label: "CM quotes", to: "/dashboard/buyer/contract-manufacturers/quotes" },
    {
      key: "agreements",
      label: "Agreements",
      to: "/dashboard/buyer/contract-manufacturers/quotes",
    },
  ],
  infra: [
    { key: "requirements", label: "My requirements", to: "/infrastructure-leasing/my-requirements" },
    { key: "enquiries", label: "My enquiries", to: "/infrastructure-leasing/my-enquiries" },
    { key: "browse", label: "Browse listings", to: "/infrastructure-leasing/listings" },
  ],
  p2p: [
    {
      key: "command",
      label: "Command centre",
      to: "/dashboard/p2p/command-centre",
      visible: (ctx) => ctx.canAccessCommandCentre || ctx.isSuperAdmin,
    },
    {
      key: "approvals",
      label: "My approvals",
      to: "/dashboard/p2p/my-approvals",
      visible: (ctx) => ctx.canApprove || ctx.isSuperAdmin || ctx.isCompanyAdmin,
    },
    {
      key: "vendors",
      label: "Pre-approved vendors",
      to: "/dashboard/p2p/preapproved-vendors",
      visible: (ctx) => ctx.canAccessPreapprovedVendors || ctx.isSuperAdmin,
    },
    {
      key: "services",
      label: "Service catalogue",
      to: "/dashboard/p2p/services",
      visible: (ctx) => ctx.canSubmitServiceRequests || ctx.isSuperAdmin,
    },
  ],
  logistics: [
    { key: "send", label: "Send logistics RFQ", to: "/logistics/rfq" },
    { key: "sent", label: "RFQs sent", to: "/logistics-rfqs-sent" },
  ],
  qa: [{ key: "quotations", label: "Approve QA quotations", to: "/buyer/qa-quotations-list" }],
  auctions: [
    { key: "browse", label: "Browse auctions", to: "/auction" },
    { key: "bids", label: "My bids", to: "/auction/my/bids" },
  ],
};

/**
 * @typedef {{
 *   user: object|null,
 *   roles: object[],
 *   p2pNav: object|null,
 * }} BuyerDashboardNavContext
 */

export function buildBuyerDashboardNavContext(p2pNav = null, options = {}) {
  const { p2pLoading = false } = options;
  const user = StorageService.getUser();
  const roles = getAvailableRoles();
  const currentRole = getCurrentRole();
  const p = p2pNav || {};
  const hasBuyerRole = roles.some((r) => r.id === 1);
  const canAccessBuyerDashboard = hasBuyerRole && isBuyerRoleSectionVisible(currentRole);

  return {
    user,
    roles,
    currentRole,
    p2pNav: p,
    p2pLoading,
    hasBuyerRole,
    canAccessBuyerDashboard,
    isSuperAdmin: !!p.is_super_admin,
    isCompanyAdmin: !!p.is_company_admin,
    canApprove: !!p.can_approve,
    canAccessCommandCentre: !!p.can_access_command_centre,
    canAccessPreapprovedVendors: !!p.can_access_preapproved_vendors,
    canSubmitServiceRequests: !!p.can_submit_service_requests,
    hasP2pAccess: !!(
      p.has_p2p_org_membership ||
      p.can_access_command_centre ||
      p.can_approve ||
      p.can_access_preapproved_vendors ||
      p.can_submit_service_requests ||
      p.can_access_sales_auction_controls ||
      p.is_super_admin ||
      p.is_company_admin ||
      p.is_platform_admin ||
      p.is_platform_team ||
      p.is_account_manager_only ||
      p.is_platform_team_console_admin
    ),
  };
}

export function resolveP2pModuleRoute(ctx) {
  if (ctx.canAccessCommandCentre || ctx.isSuperAdmin) return "/dashboard/p2p/command-centre";
  if (ctx.canApprove || ctx.isCompanyAdmin) return "/dashboard/p2p/my-approvals";
  if (ctx.canAccessPreapprovedVendors) return "/dashboard/p2p/preapproved-vendors";
  if (ctx.canSubmitServiceRequests) return "/dashboard/p2p/services";
  return "/dashboard/p2p/command-centre";
}

function moduleVisible(mod, ctx) {
  if (!ctx.canAccessBuyerDashboard) return false;
  if (typeof mod.visible === "function") return mod.visible(ctx);
  return true;
}

export function getVisibleBuyerDashboardModules(ctx) {
  return BUYER_DASHBOARD_MODULES.filter((mod) => moduleVisible(mod, ctx)).map((mod) => {
    if (mod.key === "p2p") {
      return { ...mod, to: BUYER_DASHBOARD_MODULE_ROUTES.p2p };
    }
    return mod;
  });
}

export function getVisibleBuyerDashboardModuleKeys(ctx) {
  return getVisibleBuyerDashboardModules(ctx).map((m) => m.key);
}

/** Maps overview alert cards to buyer dashboard modules. */
const OVERVIEW_ALERT_MODULES = {
  rfqs_review: ["rfq"],
  pending_approvals: ["p2p"],
  quotes_compare: ["rfq"],
  delayed: ["rfq"],
  invoices: ["rfq"],
};

/**
 * Filters overview dashboard sections to modules the logged-in user can access.
 * @param {object|null} data
 * @param {BuyerDashboardNavContext} ctx
 */
export function filterBuyerDashboardOverviewData(data, ctx) {
  if (!data) return data;
  const visibleKeys = new Set(getVisibleBuyerDashboardModuleKeys(ctx));

  const status_alerts = (data.status_alerts || []).filter((alert) => {
    const modules = OVERVIEW_ALERT_MODULES[alert.key];
    if (!modules) return true;
    return modules.some((k) => visibleKeys.has(k));
  });

  const module_summaries = (data.module_summaries || []).filter((mod) => visibleKeys.has(mod.key));

  return { ...data, status_alerts, module_summaries };
}

function pathMatchesModule(path, mod) {
  if (mod.key === "auctions") {
    if (path === "/auction") return true;
    if (path.startsWith("/auction/qa")) return false;
    return path.startsWith("/auction/");
  }
  const prefixes = [mod.to, ...(mod.pathPrefixes || [])];
  return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function getVisibleBuyerDashboardSubTabs(moduleKey, ctx) {
  const tabs = BUYER_DASHBOARD_SUB_TABS[moduleKey] || [];
  return tabs.filter((tab) => (tab.visible ? tab.visible(ctx) : true));
}

export function resolveActiveBuyerDashboardModule(pathname, modules) {
  const path = pathname || "";
  let best = modules.find((m) => m.key === "overview");
  let bestLen = 0;

  for (const mod of modules) {
    if (mod.key === "overview") {
      if (path === "/dashboard/buyer" || path === "/dashboard/buyer/") return mod;
      continue;
    }
    if (pathMatchesModule(path, mod)) {
      const prefixes = [mod.to, ...(mod.pathPrefixes || [])];
      const bestPrefix = prefixes
        .filter((prefix) => path === prefix || path.startsWith(`${prefix}/`))
        .sort((a, b) => b.length - a.length)[0];
      const len = bestPrefix?.length || 0;
      if (len >= bestLen) {
        best = mod;
        bestLen = len;
      }
    }
  }

  return best || modules[0];
}

export function isSubTabActive(pathname, tab) {
  const path = pathname || "";
  return path === tab.to || path.startsWith(`${tab.to}/`);
}
